<template>
  <BaseDataTable
    :entries="entries"
    :columns="columns"
    :loading="loading"
    :paginator-info="paginatorInfo"
    :default-page-length="defaultPageLength"
    :multi-select="multiSelect"
    :pre-selected-rows="preSelectedRows"
    class="recruiters-table"
    row-hover
    tall-rows
    preserve-selected-rows
    @search="$emit('search', $event)"
    @row-click="$emit('row-click', $event)"
    @rows-selected="$emit('rows-selected', $event)"
  >
    <template v-for="name in Object.keys($slots).filter(filterSlots)" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps || {}"></slot>
    </template>

    <template #Name="{ row, rowIndex }">
      <WdsAvatarLabel
        sm
        :path="getAvatarPath(row.avatar, row.recruiterId)"
        :initials="row.initials"
        :sub-text="textHelpers.truncate(row.name)"
        :data-test="`recruiter-name-${rowIndex}`"
      >
        {{ row.name }}
      </WdsAvatarLabel>
    </template>

    <template #Market="{ row, rowIndex }">
      <WdsStack
        direction-row
        justify-start
        gap-sm
        align-center
        :data-test="`recruiter-market-${rowIndex}`"
      >
        <WdsIcon
          v-if="row.market?.code"
          :size="16"
          :name="`flag-${row.market.code.toLowerCase()}` as WdsIconNameEnum"
          class="tw-flex-shrink-0"
        />

        <div class="tw-whitespace-nowrap">{{ row.market?.name ?? '' }}</div>
      </WdsStack>
    </template>

    <template #Fee="{ row }">{{ (row.recruiterFee ?? 0).toFixed(2) }} %</template>

    <template #OwnershipDays="{ row }">
      <div class="tw-capitalize">{{ row.recruiterOwnershipDays ?? '-' }}</div>
    </template>

    <template #Status="{ row }">
      <WdsTag sm :success="row.status === 'active'" :warning="row.status === 'invited'">
        {{ $t(upperFirst(row.status.toLowerCase())) }}
      </WdsTag>
    </template>

    <template #Tags="{ row }">
      <WdsTagGroup :tags="formattedTags(row.tags)" sm :max-tags="3" />
    </template>
  </BaseDataTable>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import upperFirst from 'lodash/upperFirst'
import {
  WdsAvatarLabel,
  WdsIcon,
  WdsStack,
  WdsTag,
  WdsTagGroup,
  WdsIconNameEnum,
} from '@worksome/wds-components'
import { getAvatarPath } from '@/utils/avatar'
import { textHelpers } from '@/utils/tools'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import BaseDataTable from '@/components/BaseDataTable'
import { graphqlFragmentValidator } from '@/utils/propValidation'
import { PAGINATOR_INFO_FRAGMENT } from '@/components/ProvidePagination.vue'
import {
  ManageWorkersInterface,
  manageWorkersKey,
} from '@/components/Contacts/CompanyRecruiter/injectionKeys'

defineEmits(['rows-selected', 'row-click', 'search'])

const props = defineProps({
  entries: {
    type: Array,
    required: true,
  },
  loading: {
    type: Boolean,
    required: true,
  },
  paginatorInfo: {
    type: Object,
    default: undefined,
    validator: (value) => graphqlFragmentValidator(PAGINATOR_INFO_FRAGMENT)(value),
  },
  additionalColumns: {
    type: Array,
    default: () => [],
  },
  multiSelect: {
    type: Boolean,
    default: false,
  },
  preSelectedRows: {
    type: Array,
    default: () => [],
  },
  maxSkills: {
    type: Number,
    default: 3,
  },
  defaultPageLength: {
    type: Number,
    default: 50,
  },
})

const { manageWorkers } = inject<ManageWorkersInterface>(manageWorkersKey, {
  manageWorkers: ref(false),
})

const standardColumns = [
  {
    key: 'name',
    slot: 'Name',
    title: $t('Name'),
    order: 'asc',
    class: 'tw-w-[30%] tw-cursor-pointer',
    orderAble: true,
  },
  {
    key: 'market',
    slot: 'Market',
    title: $t('Market'),
    order: 'asc',
    class: 'tw-w-[20%] tw-cursor-pointer',
    orderAble: true,
  },
  ...(!manageWorkers.value
    ? [
        {
          key: 'fee',
          slot: 'Fee',
          title: $t('Fee'),
          order: 'asc',
          class: ' tw-w-[10%] tw-cursor-pointer tw-text-gray-850 tw-text-[13px]',
          orderAble: true,
        },
      ]
    : []),
  {
    key: 'ownershipDays',
    slot: 'OwnershipDays',
    title: $t('Ownership days'),
    order: 'asc',
    class: 'tw-w-[10%] tw-cursor-pointer tw-text-gray-850 tw-text-[13px]',
    orderAble: true,
  },
  {
    key: 'status',
    slot: 'Status',
    title: $t('Status'),
    order: 'asc',
    class: 'tw-w-[10%] tw-cursor-pointer tw-text-gray-850 tw-text-[13px]',
    orderAble: true,
  },
  {
    key: 'tags',
    slot: 'Tags',
    title: $t('Tags'),
    class: 'tw-w-[20%] tw-cursor-pointer tw-text-gray-850 tw-text-[13px]',
  },
]

const columns = computed(() => [...standardColumns, ...props.additionalColumns])

const filterSlots = (slotName: string) => !standardColumns.map((col) => col.slot).includes(slotName)

const formattedTags = (tags) =>
  tags?.map((tag) => ({
    label: tag.name,
    primary: false,
  }))
</script>

<style scoped lang="scss"></style>
